import { useState, useCallback, useEffect, useRef } from "react";
import { MAX_OPEN_TABS, NOTIFICATION_TIMEOUT } from "../constants";

/**
 * Hook to manage editor tabs and their state
 * @returns {Object} Object containing tab management functions and state
 */
export const useTabManagement = () => {
  const [openTabs, setOpenTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [modifiedFiles, setModifiedFiles] = useState(new Set());
  const [fileContent, setFileContent] = useState("");
  const [originalContent, setOriginalContent] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [notification, setNotification] = useState(null);

  // Auto-clear notifications after timeout
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, NOTIFICATION_TIMEOUT);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Open a new tab or switch to existing one
  const openTab = useCallback((file, content) => {
    let tabOpened = true;

    setOpenTabs((prev) => {
      const existingTabIndex = prev.findIndex((tab) => tab.path === file.path);

      if (existingTabIndex === -1) {
        // Tab doesn't exist, check if we can add it
        if (prev.length >= MAX_OPEN_TABS) {
          setNotification(
            "Maximum of 5 files can be open at once. Please close a tab first.",
          );
          tabOpened = false;
          return prev; // Return unchanged
        }

        // Add new tab (ensure no duplicates by filtering first)
        const newTab = {
          ...file,
          content,
          lastModified: new Date(),
        };
        // Filter out any existing tab with the same path (defensive programming)
        const filteredTabs = prev.filter((tab) => tab.path !== file.path);
        return [...filteredTabs, newTab];
      } else {
        // Update existing tab content
        const updatedTabs = [...prev];
        updatedTabs[existingTabIndex] = {
          ...updatedTabs[existingTabIndex],
          content,
          lastModified: new Date(),
        };
        return updatedTabs;
      }
    });

    // Only update active tab if tab was opened/updated successfully
    if (tabOpened) {
      setActiveTab({ path: file.path, name: file.name });
      setFileContent(content);
      setOriginalContent(content);
      setIsDirty(false);
    }

    return tabOpened;
  }, []);

  // Close a tab
  const closeTab = useCallback(
    (tabPath, e) => {
      if (e) {
        e.stopPropagation();
      }

      const newTabs = openTabs.filter((tab) => tab.path !== tabPath);
      setOpenTabs(newTabs);

      // Remove from modified files set
      setModifiedFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(tabPath);
        return newSet;
      });

      // Update active tab if closing the current one
      if (activeTab && activeTab.path === tabPath) {
        if (newTabs.length > 0) {
          const newActiveTab = newTabs[newTabs.length - 1];
          setActiveTab({ path: newActiveTab.path, name: newActiveTab.name });
          setFileContent(newActiveTab.content);
          setOriginalContent(newActiveTab.content);
          setIsDirty(false);
        } else {
          setActiveTab(null);
          setFileContent("");
          setOriginalContent("");
          setIsDirty(false);
        }
      }
    },
    [openTabs, activeTab],
  );

  // Switch to a different tab
  const switchToTab = useCallback(
    (tabPath) => {
      const tab = openTabs.find((tab) => tab.path === tabPath);
      if (tab) {
        setActiveTab({ path: tab.path, name: tab.name });
        setFileContent(tab.content);
        setOriginalContent(tab.content);

        // Check if this file is modified
        const isModified = modifiedFiles.has(tab.path);
        setIsDirty(isModified);
      }
    },
    [openTabs, modifiedFiles],
  );

  // Update tab content
  const updateTabContent = useCallback(
    (tabPath, content) => {
      setOpenTabs((prev) =>
        prev.map((tab) => (tab.path === tabPath ? { ...tab, content } : tab)),
      );

      // If this is the active tab, update the current content
      if (activeTab && activeTab.path === tabPath) {
        setFileContent(content);
      }
    },
    [activeTab],
  );

  // Handle content changes in the editor
  const handleContentChange = useCallback(
    (value) => {
      setFileContent(value);
      const dirty = value !== originalContent;
      setIsDirty(dirty);

      if (activeTab) {
        if (dirty) {
          setModifiedFiles((prev) => new Set([...prev, activeTab.path]));
        } else {
          setModifiedFiles((prev) => {
            const newSet = new Set(prev);
            newSet.delete(activeTab.path);
            return newSet;
          });
        }

        // Update the tab's content in memory
        updateTabContent(activeTab.path, value);
      }
    },
    [originalContent, activeTab, updateTabContent],
  );

  // Mark current file as saved
  const markAsSaved = useCallback(() => {
    if (activeTab) {
      setOriginalContent(fileContent);
      setIsDirty(false);
      setModifiedFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(activeTab.path);
        return newSet;
      });
    }
  }, [activeTab, fileContent]);

  // Check if a specific file has unsaved changes
  const isFileModified = useCallback(
    (filePath) => {
      return modifiedFiles.has(filePath);
    },
    [modifiedFiles],
  );

  // Get tab by path
  const getTab = useCallback(
    (filePath) => {
      return openTabs.find((tab) => tab.path === filePath);
    },
    [openTabs],
  );

  // Close all tabs
  const closeAllTabs = useCallback(() => {
    setOpenTabs([]);
    setActiveTab(null);
    setFileContent("");
    setOriginalContent("");
    setIsDirty(false);
    setModifiedFiles(new Set());
  }, []);

  // Close other tabs (keep only the specified one)
  const closeOtherTabs = useCallback(
    (tabPath) => {
      const tabToKeep = openTabs.find((tab) => tab.path === tabPath);
      if (tabToKeep) {
        setOpenTabs([tabToKeep]);
        setActiveTab({ path: tabToKeep.path, name: tabToKeep.name });
        setFileContent(tabToKeep.content);
        setOriginalContent(tabToKeep.content);

        // Check if this file is modified
        const isModified = modifiedFiles.has(tabToKeep.path);
        setIsDirty(isModified);

        // Keep only this file in modified set if it was modified
        if (isModified) {
          setModifiedFiles(new Set([tabToKeep.path]));
        } else {
          setModifiedFiles(new Set());
        }
      }
    },
    [openTabs, modifiedFiles],
  );

  return {
    // State
    openTabs,
    activeTab,
    modifiedFiles,
    fileContent,
    originalContent,
    isDirty,
    notification,

    // State setters
    setFileContent,
    setOriginalContent,
    setIsDirty,
    setNotification,

    // Operations
    openTab,
    closeTab,
    switchToTab,
    updateTabContent,
    handleContentChange,
    markAsSaved,
    isFileModified,
    getTab,
    closeAllTabs,
    closeOtherTabs,
  };
};
